import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, TextField, Button, Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
// @mui/icons-material
import SendIcon from '@mui/icons-material/Send';
import GroupIcon from '@mui/icons-material/Group';
import AttachmentIcon from '@mui/icons-material/Attachment';
import ReactQuill from 'react-quill';
import axios from 'axios';
// NOTE ON RICH TEXT EDITOR:
// In your environment, replace the 'textarea' below with the actual ReactQuill component
// (like the one you used for 'summary' in NewsManagement) to handle rich content.
// This example uses a standard input styled to match the ReactQuill dimensions.

const Emailcommunication = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [includeUdyogConnect, setIncludeUdyogConnect] = useState(false);
  const [includeTechSummit, setIncludeTechSummit] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const fileInputRef = useRef(null); // Ref for file input

  // Function to simulate converting HTML to plain text
  const convertHtmlToText = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    let text = tempDiv.textContent || tempDiv.innerText || "";
    return text.trim();
  };

  const handleFileChange = (event) => {
    setAttachments(Array.from(event.target.files));
  };

  const handleSendEmail = useCallback(async (e) => {
    e.preventDefault();
    
    if (!subject || !htmlBody) {
      setStatus({ type: 'error', message: 'Subject and Email Body are required.' });
      return;
    }
    if (!includeUdyogConnect && !includeTechSummit) {
        setStatus({ type: 'error', message: 'Please select at least one recipient group.' });
        return;
    }

    setIsLoading(true);
    setStatus(null);

    // --- 1. Constructing the Multipart Form Data Payload ---
    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('htmlBody', htmlBody);
    formData.append('textBody', convertHtmlToText(htmlBody)); 
    formData.append('includeUdyogConnect', includeUdyogConnect.toString());
    formData.append('includeTechSummit', includeTechSummit.toString());

    attachments.forEach(file => {
      formData.append('attachments', file); 
    });

    // --- 2. Mock API Call to BFF Endpoint ---
    const BFF_ENDPOINT = '/api/admin/send-mass-email'; 

    try {
      // Mock fetch call (replace with your actual API endpoint)
    //   const response = await axios.post(BFF_ENDPOINT, {
    //     method: 'POST',
    //     body: formData, 
    //   });

    const response = await axios.post(`${API_BASE}/api/admin/send-mass-email`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
        });
      if (response.status===202) {
        setStatus({ 
            type: 'success', 
            message: 'Mass email successfully sent/queued!'
        });
        // Clear form after successful send
        setSubject('');
        setHtmlBody('<p>Enter your email content here.</p>');
        setIncludeUdyogConnect(false);
        setIncludeTechSummit(false);
        setAttachments([]);
        if (fileInputRef.current) {
             fileInputRef.current.value = '';
        }
      } else {
        const errorData = await response.json();
        setStatus({ 
            type: 'error', 
            message: `Send failed: 'Server error.'}` 
        });
      }
    } catch (error) {
      console.error('Network or system error:', error);
      setStatus({ type: 'error', message: 'Could not connect to server or unexpected error occurred.' });
    } finally {
      setIsLoading(false);
    }
  }, [subject, htmlBody, includeUdyogConnect, includeTechSummit, attachments]);

  const statusClasses = useMemo(() => {
    if (!status) return {};
    return status.type === 'success' 
      ? { backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50', color: '#1b5e20' } // Light Green/Dark Green
      : { backgroundColor: '#ffebee', borderLeft: '4px solid #f44336', color: '#b71c1c' }; // Light Red/Dark Red
  }, [status]);
  
  const fileList = useMemo(() => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {attachments.map(file => (
            <Box key={file.name} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                fontSize: 12, 
                bgcolor: 'grey.100', 
                px: 1.5, py: 0.5, 
                borderRadius: '9999px', // rounded-full 
                color: 'grey.700'
            }}>
                <AttachmentIcon sx={{ fontSize: 12, mr: 0.5, color: 'grey.500' }} />
                {file.name}
            </Box>
        ))}
    </Box>
  ), [attachments]);

  return (
    <Box 
        className="min-h-screen bg-gray-50 p-4 sm:p-8 font-sans antialiased" 
        sx={{ bgcolor: 'background.default' }}
    >
      <Box sx={{ 
          maxWidth: '800px', 
          mx: 'auto', 
          bgcolor: 'white', 
          p: { xs: 3, sm: 6 }, 
          boxShadow: 3, 
          borderRadius: 2, 
          border: '1px solid #e0e0e0' 
      }}>
        <Box component="header" sx={{ mb: 4 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <SendIcon sx={{ fontSize: 32, mr: 1.5 }} color="primary" />
            Admin: Mass Communication Sender
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Compose and send announcements to selected user groups via BCC.
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSendEmail} sx={{ '& > div': { mb: 3 } }}>
          
          {/* Email Subject */}
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            required
            placeholder="e.g., Important Update on Tech Summit Schedule"
            sx={{ mt: 0 }}
          />

          {/* BCC Recipient Selection */}
          <Box sx={{ 
              p: 2, 
              border: '1px solid', 
              borderColor: 'primary.light', 
              borderRadius: 1, 
              bgcolor: 'primary.lightest', // Simulated light background 
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.dark', display: 'flex', alignItems: 'center', mb: 1 }}>
                <GroupIcon sx={{ fontSize: 18, mr: 1 }} color="primary" />
                Select BCC Recipient Groups
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeUdyogConnect}
                    onChange={(e) => setIncludeUdyogConnect(e.target.checked)}
                    name="udyog-connect-users"
                    color="primary"
                  />
                }
                label="Udyog Connect Users"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeTechSummit}
                    onChange={(e) => setIncludeTechSummit(e.target.checked)}
                    name="techsummit-users"
                    color="primary"
                  />
                }
                label="TechSummit Registered Users"
              />
            </Box>
          </Box>
          
          {/* Email Body (Rich Text Editor Placeholder) */}
          <Box>
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 'medium', mb: 0.5 }}>
              Email Body (Rich Text)
            </Typography>
            <ReactQuill 
                id="email-body"
                theme="snow" 
                value={htmlBody} 
                onChange={setHtmlBody} 
               style={{
                width: '100%',
                border: '1px solid rgba(0, 0, 0, 0.23)', // MUI-like border
                borderRadius: '4px',
                boxSizing: 'border-box',
                fontFamily: 'Roboto, sans-serif',
                minHeight: '144px', // Matches the height: 144px from your sample
                height: 250, // ~6 rows
                overflowY: 'auto',
                boxSizing: 'border-box',
                marginBottom: 16, // Like sx={{ mb: 2 }}
              }}
                modules={{
                toolbar: [
                    [{ header: [1, 2, false] }],
                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['clean'],
                ],
                }}
            />
          </Box>

          {/* Attachments */}
          <Box sx={{ 
              p: 2, 
              border: '1px solid', 
              borderColor: 'primary.light', 
              borderRadius: 1, 
              bgcolor: 'primary.lightest', 
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.dark', display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttachmentIcon sx={{ fontSize: 18, mr: 1 }} color="primary" />
                Attachments ({attachments.length})
            </Typography>
            
            <input
              ref={fileInputRef}
              id="attachments"
              type="file"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            
            <Button 
                variant="outlined" 
                onClick={() => fileInputRef.current.click()}
                startIcon={<AttachmentIcon />}
                size="small"
            >
                Browse Files
            </Button>

            {fileList}
          </Box>

          {/* Status Message */}
          {status && (
            <Box sx={{ p: 2, borderRadius: 1, boxShadow: 1, ...statusClasses }} role="alert">
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {status.type === 'success' ? 'Success' : 'Error'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{status.message}</Typography>
            </Box>
          )}

          {/* Send Button */}
          <Button
            type="submit"
            disabled={isLoading}
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{ mt: 4, py: 1.5, fontSize: '1.1rem' }}
          >
            {isLoading ? 'Sending...' : 'Send Mass Email'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Emailcommunication;